import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import type { GuardrailReading, RuleReading, TurnDefect } from '@/lib/conversation-model';
import { turnDefect } from '@/lib/conversation-model';
import { GUARDRAIL_ACTION_LABEL } from '@/lib/rules-labels';
import type { SessionTurn } from '@matter_extractor/shared';
import { TurnLayerShell } from './layer-shell';
import { FAREWELL_NOT_EVALUATED, REREAD_NOT_EVALUATED } from './sources';

/**
 * The Garde-fous et règles layer of one turn (§8.5, §12.3).
 *
 * ## Two lists that look alike and answer two different questions
 *
 * A GUARDRAIL is judged on the person's turn and carries a series: « le moteur
 * tient la série et la remet à zéro dès qu'un tour rend faux ». Its action is
 * the engine's. A RULE is judged on the last message of the AI and carries
 * nothing: « sans aucune action », observation and nothing else. Drawing them in
 * one table would suggest the second does something, which is exactly the
 * misreading §8.5 spends a paragraph preventing - so they are two blocks, each
 * with its own header saying who judges what.
 *
 * ## The series comes off the turn, the N off Réglages
 *
 * `snapshot.guardrailStreaks` is state and rides on the turn (§8.7). The
 * threshold N and the detection sentence live in Réglages and are frozen by
 * nothing, so a guardrail deleted since shows its identifier and no N rather
 * than disappearing: the turn judged it, and a line that vanished would make the
 * series of the turn beside it unexplainable.
 *
 * ## A turn with no reading is named by its DEFECT
 *
 * `turnDefect` and never `turn.malformed` (issue #96): this fold was the one
 * that kept saying « scribe illisible » on a turn the provider had cut, which
 * is the sentence the whole lot exists to stop writing.
 */

export interface LayerGardefousProps {
  turn: SessionTurn;
  guardrails: readonly GuardrailReading[];
  rules: readonly RuleReading[];
  /** The farewell turn (issue #163): nothing was evaluated on it (H81). */
  farewell: boolean;
  /**
   * The reread row (issue #165): the scribe read it, but its guardrails and
   * rules are ignored (H96) - nothing was evaluated here either.
   */
  reread: boolean;
  open: boolean;
  onToggle: () => void;
}

function hintOf(
  defect: TurnDefect | null,
  guardrails: readonly GuardrailReading[],
  rules: readonly RuleReading[],
): string {
  // What THIS layer has to say about a defect: nothing was evaluated. The cause
  // is `turnDefect`'s, so a cut turn is named here too (issue #96) - reading
  // `turn.malformed` left this fold calling a truncated turn « illisible ».
  if (defect !== null) return `non évalués, ${defect.cause}`;
  const fired = guardrails.filter((guardrail) => guardrail.triggered).length;
  const broken = rules.filter((rule) => rule.violated).length;
  return [
    fired === 0
      ? 'rien détecté'
      : `${fired} garde-fou${fired > 1 ? 's' : ''} détecté${fired > 1 ? 's' : ''}`,
    broken === 0
      ? 'règles respectées'
      : `${broken} règle${broken > 1 ? 's' : ''} enfreinte${broken > 1 ? 's' : ''}`,
  ].join(', ');
}

export function LayerGardefous({
  turn,
  guardrails,
  rules,
  farewell,
  reread,
  open,
  onToggle,
}: LayerGardefousProps) {
  // Null on a readable turn; « Malformé » or « Tronqué » otherwise (issue #96).
  const defect = turnDefect(turn);
  return (
    <TurnLayerShell
      layer="gardefous"
      hint={
        farewell
          ? FAREWELL_NOT_EVALUATED
          : reread
            ? REREAD_NOT_EVALUATED
            : hintOf(defect, guardrails, rules)
      }
      open={open}
      onToggle={onToggle}
    >
      {farewell ? (
        <p>
          <b>Tour d’adieu, non évalué.</b> Personne ne répond à l’au revoir : aucun garde-fou ni
          aucune règle n’est jugé sur ce tour, et les séries restent où le tour précédent les a
          laissées (H81).
        </p>
      ) : reread ? (
        <p>
          <b>Relecture à l’entrée, non évaluée.</b> Personne n’a parlé sur ce tour : ce que le
          scribe rapporte des garde-fous et des règles est ignoré, aucun incident n’est levé, et les
          séries restent où le tour précédent les a laissées (H96).
        </p>
      ) : defect !== null ? (
        <p>
          <b>{defect.label}</b> ({defect.cause}). Aucune lecture ce tour : les séries des garde-fous
          ne montent ni ne redescendent (§8.1).
        </p>
      ) : (
        <>
          <section className="flex flex-col gap-1.5">
            <h5 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
              Garde-fous · le scribe détecte, le code compte
            </h5>
            {guardrails.length === 0 ? (
              <p className="text-gray-600 text-xs">
                Aucun garde-fou n’était posé quand ce tour a été joué.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {guardrails.map((guardrail) => (
                  <li
                    key={guardrail.guardrailId}
                    data-testid={SESSIONS_TEST_IDS.turnGuardrail}
                    data-guardrail-id={guardrail.guardrailId}
                    className="grid gap-x-4 gap-y-0.5 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-baseline"
                  >
                    <span className="min-w-0 break-words text-gray-900">
                      {guardrail.detection}
                      {guardrail.action === null ? (
                        <span className="text-gray-500 text-xs"> · retiré de Réglages depuis</span>
                      ) : (
                        <span className="text-gray-500 text-xs">
                          {' '}
                          · {GUARDRAIL_ACTION_LABEL[guardrail.action.kind].toLowerCase()}
                        </span>
                      )}
                    </span>
                    <span
                      className={
                        guardrail.triggered ? 'font-bold text-jaune-deep' : 'text-gray-500 text-sm'
                      }
                    >
                      {guardrail.triggered ? 'détecté' : 'non'}
                    </span>
                    <span className="text-gray-600 text-xs tabular-nums">
                      série {guardrail.streak}
                      {guardrail.consecutiveTurns === null
                        ? ''
                        : ` sur ${guardrail.consecutiveTurns}`}
                      {guardrail.incident ? ' → incident signalé' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-1.5">
            <h5 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
              Règles · l’interviewer les reçoit, le scribe observe
            </h5>
            {rules.length === 0 ? (
              <p className="text-gray-600 text-xs">
                Aucune règle globale n’était posée quand ce tour a été joué.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {rules.map((rule) => (
                  <li
                    key={rule.ruleId}
                    data-testid={SESSIONS_TEST_IDS.turnRule}
                    data-rule-id={rule.ruleId}
                    className="grid gap-x-4 gap-y-0.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline"
                  >
                    <span className="min-w-0 break-words text-gray-900">
                      {rule.text}
                      {rule.resolved ? null : (
                        <span className="text-gray-500 text-xs"> · retirée de Réglages depuis</span>
                      )}
                    </span>
                    <span
                      className={
                        rule.violated ? 'font-bold text-rouge-deep' : 'text-gray-500 text-sm'
                      }
                    >
                      {rule.violated ? 'enfreinte' : 'respectée'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-gray-500 text-xs">
              Observation seule : une règle enfreinte ne déclenche rien (§8.5).
            </p>
          </section>
        </>
      )}
    </TurnLayerShell>
  );
}
